import { useSubscription } from '@apollo/client';
import { css } from '@emotion/react';
import { Box, Skeleton } from '@mui/material';
import { ClubLogo, vars, fontSize } from '@scorenco/components';
import { LiveListenScoreDocument, Types } from '@scorenco/core';

const style = {
  root: (theme) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .event-card_logo {
      width: 1.25rem;
      height: 2rem;
    }

    .event_card_give-score {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .event-card_scoreboard_live {
      text-align: center;
      font-size: 8px;
      font-weight: bold;
      color: #cc062a;
      text-transform: uppercase;
      margin-top: 0.5rem;
      margin-bottom: 0.25rem;
      display: flex;
      justify-content: center;
      align-items: center;

      &::before {
        content: '';
        height: 0.5rem;
        width: 0.5rem;
        margin-right: 0.25rem;
        background-color: #cc062a;
        border-radius: 9999px;
        display: inline-block;
      }
    }

    .event-card_scores_and_shoutouts {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .event-card_scoreboard_shoutouts {
      margin: 0;
      font-size: 0.8rem;
      text-align: center;
      font-weight: bold;
      margin-top: 0.25rem;
    }

    .event-card_scores {
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
      display: flex;
    }

    .event-card_date {
      text-align: center;
      ${fontSize.xs};
      line-height: 1.2;
      font-style: italic;

      > *:first-of-type {
        ${fontSize.s};
        line-height: 1.2;
        font-style: normal;
        font-weight: 500;
      }
    }

    .event-card_team {
      flex: 1;
      ${fontSize.base};
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 ${theme.spacing(2)};
      text-align: center;
    }
  `,
  score: css`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    border-radius: 0.25rem;
    text-align: center;
    background-color: var(${vars.color.secondary100});
    width: 1.75rem;
    font-family: var(${vars.font.alt});
    margin: 0 2px;
    letter-spacing: -0.1em;

    &:first-of-type {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  `,
  scoreIsWinner: css`
    background-color: var(${vars.color.secondary100});
    color: var(${vars.color.primary500});
  `,
  scoreSmall: css`
    font-size: 16px;
  `,
};

type ScoreProps = {
  score?: number;
  isWinner: boolean;
};

const Score = ({ score, isWinner }: ScoreProps) => {
  if (score === null) {
    return null;
  }

  return (
    <div
      css={[
        style.score,
        isWinner && style.scoreIsWinner,
        score >= 100 && style.scoreSmall,
      ]}
    >
      {score}
    </div>
  );
};

export type GameCardProps = {
  game?: { game: Types.Tournaments_Game };
};

export const GameCard = ({ game, ...rest }: GameCardProps) => {
  const event_detail = game?.game?.event?.detail?.[0];
  const homeTeam = game?.game?.home_engagement;
  const awayTeam = game?.game?.away_engagement;
  const homeScore = event_detail?.teams?.[0]?.score || game?.game?.home_score;
  const awayScore = event_detail?.teams?.[1]?.score || game?.game?.away_score;
  const status = game?.game?.status;
  const homeShoutout = game?.game?.home_shootout;
  const awayShoutout = game?.game?.away_shootout;

  let scoreboard;
  let eventTime;
  if (status === 'to_come') {
    const eventDate = new Date(game?.game?.date);
    const time = new Date(game?.game?.date);
    eventTime = time.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    scoreboard = (
      <div className="event-card_date">
        <div>
          {eventDate.toLocaleDateString([], {
            month: 'short',
            day: '2-digit',
          })}
        </div>
        <div>{eventTime}</div>
      </div>
    );
  } else if (homeScore === null && awayScore === null) {
    scoreboard = (
      <div
        className="event_card_give-score"
        dangerouslySetInnerHTML={{
          __html: 'Donner <br />le score',
        }}
      />
    );
  } else {
    scoreboard = (
      <div className="event-card_scoreboard">
        {status === 'live' && (
          <div className="event-card_scoreboard_live">
            <span>{status}</span>
          </div>
        )}
        <div className="event-card_scores_and_shoutouts">
          <div className="event-card_scores">
            <Score
              score={homeScore}
              isWinner={status === 'finish' && homeScore > awayScore}
            />
            <Score
              score={awayScore}
              isWinner={status === 'finish' && homeScore < awayScore}
            />
          </div>
          {homeShoutout && awayShoutout ? (
            <p className="event-card_scoreboard_shoutouts">
              TAB {homeShoutout} - {awayShoutout}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  useSubscription(LiveListenScoreDocument, {
    variables: { eventId: Number(game?.event_id) },
    skip: !game?.event_id,
  });

  return (
    <Box css={style.root} {...rest}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '8rem',
        }}
      >
        <ClubLogo
          className="event-card_logo"
          logo={homeTeam?.team?.logo}
          name={homeTeam?.team?.name || homeTeam?.name}
          shieldColor="home"
        />

        <Box className="event-card_team">
          {homeTeam?.team?.name || homeTeam?.name || <Skeleton />}
        </Box>
      </Box>

      {scoreboard}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '8rem',
        }}
      >
        <ClubLogo
          className="event-card_logo"
          logo={awayTeam?.team?.logo}
          name={awayTeam?.team?.name || homeTeam?.name}
          shieldColor="away"
        />
        <Box className="event-card_team event-card_team-away">
          {awayTeam?.team?.name || awayTeam?.name || <Skeleton />}
        </Box>
      </Box>
    </Box>
  );
};
